import * as React from 'react';

import {
  BodySmall,
  BodyMediumBold,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {SimpleButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {ChartWrapper} from '@spaced-out/ui-design-system/lib/components/Charts/ChartWrapper';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';

import {classify} from 'src/utils/classify';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {ByodKpiBox} from 'src/components/analytics-genesis/byod/components/byod-kpi-box';
import {ByodKpiTrendIndicator} from 'src/components/analytics-genesis/byod/components/byod-kpi-trend-indicator';
import {ByodTable} from 'src/components/analytics-genesis/byod/components/byod-table';
import type {FetchDataParams} from 'src/components/analytics-genesis/byod/components/byod-table';
import type {TableSearch} from 'src/components/analytics-genesis/byod/components/byod-table-filter';
import {ByodChart} from 'src/components/analytics-genesis/byod/components/byod-chart';
import {resolveChartConfig} from 'src/components/analytics-genesis/byod/utils/chart-config';
import {formatKpiValue} from 'src/components/analytics-genesis/byod/utils/format-kpi-value';
import type {
  WidgetConfig,
  ChartStyle,
  PreviewResponse,
  TileFetchStatus,
  KpiSemantic,
} from 'src/types/byod';
import {WIDGET_TYPE, isKpiSemantic} from 'src/types/byod';
import {
  SHOW_TREND_CONFIG_KEY,
  KPI_SEMANTIC_CONFIG_KEY,
} from 'src/components/analytics-genesis/byod/constants';

// CSS lives with the builder canvas (also shared by section-row). The shared
// tile imports it in place so tile styles have ONE source consumed by the
// builder canvas AND the embeddable view.
import kpiCss from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/dashboard-builder-kpi-tile.css';
import vizCss from 'src/components/analytics-genesis/byod/pages/dashboard-builder/canvas/dashboard-builder-viz-tile.css';


const CHART_TILE_HEIGHT = 350;

export type {FetchDataParams};

/**
 * Store-agnostic presentational tile — the ONE render path shared by the builder
 * canvas and the embeddable `<ByodDashboardView>`. Routes by `widget_type` and
 * renders the leaf renderers (`ByodKpiBox` / `ByodChart` / `ByodTable`) across
 * skeleton / error / loaded states. Reads NOTHING from any store: the fetch
 * result and any edit affordances arrive as props.
 *
 * - Builder: a thin adapter feeds it from the store + wires edit/mutation
 *   handlers + `canEdit`.
 * - Embed: renders it read-only (`canEdit={false}`, no edit handlers).
 *
 * A change here (or in the tile CSS / leaf renderers) reflects in both.
 * Derivations (chart config, selected columns, KPI value, active filter count)
 * are computed from `widget` + `data` so both surfaces get them identically.
 */
export type DashboardTileProps = {
  tileId: string;
  widget: WidgetConfig;
  canEdit: boolean;
  /** Viewport ref from the tile-data hook — drives lazy fetch + scroll. */
  tileRef: (el: Element | null) => void;

  // Fetch result (from useTileData).
  data: PreviewResponse | undefined;
  isError: boolean;
  isFetching: boolean;
  error: Error | null;
  fetchStatus: TileFetchStatus;
  onRetry: () => void;

  // Edit affordances — rendered only when `canEdit` and the handler is given.
  isRemoving?: boolean;
  onEdit?: (tileId: string) => void;
  onRemove?: (tileId: string) => void;
  // Chart-only.
  onChartStyleChange?: (kind: ChartStyle) => void;
  // Table-only.
  onColumnsChange?: (cols: string[]) => void;
  /** Entity-attribute column selection (from the response's `all_attributes_columns`). */
  onEntityColumnsChange?: (cols: string[]) => void;
  onFetchData?: (params: FetchDataParams) => void;
  onExport?: () => void;
  /** Opens the schedule panel — renders a bell button in the table toolbar. */
  onSchedule?: () => void;
  /**
   * Table-only view-time search (`{search_column?, search_text}`) and its change
   * handler. Omit the handler to hide the Filter button. The adapter folds this
   * into the tile's `/query` `widget_config` and re-fetches.
   */
  search?: TableSearch;
  onSearchChange?: (search: TableSearch) => void;
  /** Bumped to reset table pagination to page 1 (e.g. when the search changes). */
  tableResetKey?: number;
};

function TileErrorRetry({
  message,
  onRetry,
  testIdPrefix,
}: {
  /** Error text. Falls back to a generic "Failed to load report" when empty. */
  message?: string;
  onRetry: () => void;
  testIdPrefix: string;
}) {
  const {t} = useLabelTranslation();
  // Never render a blank error line — an empty/absent message still gets a
  // generic reason so the user knows something failed.
  const text = message || t('Failed_to_load_report', 'Failed to load report');
  return (
    <div
      className={vizCss.tileErrorRetry}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <BodySmall color="danger">{text}</BodySmall>
      <Button
        type="ghost"
        size="small"
        iconLeftName="arrow-rotate-right"
        iconLeftType="regular"
        onClick={onRetry}
        testId={`${testIdPrefix}-retry`}
      >
        {t('Retry', 'Retry')}
      </Button>
    </div>
  );
}

function KpiTileEllipsisMenu({
  tileId,
  label,
  canEdit,
  isRemoving,
  onEdit,
  onRemove,
}: {
  tileId: string;
  label: string;
  canEdit: boolean;
  isRemoving?: boolean;
  onEdit?: (tileId: string) => void;
  onRemove?: (tileId: string) => void;
}) {
  const {t} = useLabelTranslation();
  const [menuOpen, setMenuOpen] = React.useState(false);

  if (!canEdit) {
    return null;
  }

  const forceVisible = menuOpen || Boolean(isRemoving);

  return (
    <div
      className={classify(
        kpiCss.kpiTileEllipsisWrap,
        forceVisible && kpiCss.kpiTileEllipsisWrapVisible,
      )}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {!isRemoving && (
        <SimpleButtonDropdown
          type="ghost"
          size="small"
          iconLeftName="ellipsis-v"
          ariaLabel={t('Widget_actions_for_name', {
            name: label,
            defaultValue: `Widget actions for ${label}`,
          })}
          options={[
            {
              key: 'edit',
              label: t('Edit_widget', 'Edit widget'),
              iconLeft: 'pencil',
            },
            {
              key: 'remove',
              label: t('Remove_widget', 'Remove widget'),
              iconLeft: 'trash',
            },
          ]}
          onOptionSelect={(opt) => {
            if (opt.key === 'edit') {
              onEdit?.(tileId);
            } else if (opt.key === 'remove') {
              void onRemove?.(tileId);
            }
          }}
          onMenuOpen={() => setMenuOpen(true)}
          onMenuClose={() => setMenuOpen(false)}
          isFluid={false}
        />
      )}
    </div>
  );
}

/** Edit/remove ellipsis dropdown for the table + chart (viz) tiles. */
function VizTileActionsMenu({
  tileId,
  label,
  onEdit,
  onRemove,
}: {
  tileId: string;
  label: string;
  onEdit?: (tileId: string) => void;
  onRemove?: (tileId: string) => void;
}) {
  const {t} = useLabelTranslation();
  return (
    <span
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <SimpleButtonDropdown
        type="ghost"
        size="small"
        iconLeftName="ellipsis-v"
        ariaLabel={t('Widget_actions_for_name', {
          name: label,
          defaultValue: `Widget actions for ${label}`,
        })}
        options={[
          {
            key: 'edit',
            label: t('Edit_widget', 'Edit widget'),
            iconLeft: 'pencil',
          },
          {
            key: 'remove',
            label: t('Remove_widget', 'Remove widget'),
            iconLeft: 'trash',
          },
        ]}
        onOptionSelect={(opt) => {
          if (opt.key === 'edit') {
            onEdit?.(tileId);
          } else if (opt.key === 'remove') {
            void onRemove?.(tileId);
          }
        }}
        isFluid={false}
        testId={`byod-tile-actions-${tileId}`}
      />
    </span>
  );
}

function KpiTileView(props: DashboardTileProps) {
  const {
    tileId,
    widget,
    canEdit,
    tileRef,
    data,
    isError,
    isFetching,
    error,
    fetchStatus,
    onRetry,
    isRemoving,
    onEdit,
    onRemove,
  } = props;
  const {t} = useLabelTranslation();
  const label = widget?.label ?? tileId;
  const naText = t('NA', 'N/A');

  if (fetchStatus === 'idle' && !data && !isError) {
    return (
      <div
        ref={tileRef}
        className={kpiCss.kpiTile}
        data-tile-id={tileId}
        data-testid={`byod-kpi-tile-${tileId}-skeleton`}
      />
    );
  }

  if (isError) {
    return (
      <div
        ref={tileRef}
        className={kpiCss.kpiTile}
        data-tile-id={tileId}
        data-testid={`byod-kpi-tile-${tileId}`}
      >
        {canEdit ? (
          <div className={kpiCss.kpiTileMenuRow}>
            <KpiTileEllipsisMenu
              tileId={tileId}
              label={label}
              canEdit={canEdit}
              isRemoving={isRemoving}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          </div>
        ) : null}
        <div className={kpiCss.kpiTileError}>
          <BodySmall color="danger" className={kpiCss.kpiTileErrorMessage}>
            {/* Distinct from naText — an error must not look like a null value. */}
            {error?.message ||
              t('Failed_to_load_report', 'Failed to load report')}
          </BodySmall>
          {isFetching ? (
            <CircularLoader size="small" />
          ) : (
            <Button
              type="ghost"
              size="small"
              iconLeftName="arrow-rotate-right"
              iconLeftType="regular"
              onClick={onRetry}
              testId={`byod-kpi-tile-${tileId}-retry`}
            >
              {t('Retry', 'Retry')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  const kpiData = data?.widget_type === WIDGET_TYPE.KPI ? data : null;
  const middleContent: React.ReactNode =
    kpiData && kpiData.value != null ? formatKpiValue(kpiData.value) : naText;
  const isLoading = (!data && !isError) || isFetching;

  // Comparison-period trend line under the value — shown when the widget was
  // configured with `show_trend` (persisted in widget_config, forwarded to
  // /query so the backend returns the trend fields). ByodKpiTrendIndicator
  // renders nothing when there's no percentage, so a KPI without trend data
  // stays value-only even when the toggle is on.
  const showTrend =
    widget?.widget_config_json?.[SHOW_TREND_CONFIG_KEY] === true;

  // Persisted KPI icon accent (widget_config_json). Falls back to neutral for
  // widgets saved before the picker existed or with an unrecognized value.
  const rawSemantic = widget?.widget_config_json?.[KPI_SEMANTIC_CONFIG_KEY];
  const kpiSemantic: KpiSemantic = isKpiSemantic(rawSemantic)
    ? rawSemantic
    : 'neutral';
  const trendContent =
    showTrend && kpiData ? (
      <ByodKpiTrendIndicator
        direction={kpiData.trend_direction ?? null}
        percentage={kpiData.trend_percentage ?? null}
      />
    ) : undefined;

  return (
    <div
      ref={tileRef}
      className={kpiCss.kpiTile}
      data-tile-id={tileId}
      data-testid={`byod-kpi-tile-${tileId}`}
    >
      {canEdit ? (
        <div className={kpiCss.kpiTileMenuRow}>
          <KpiTileEllipsisMenu
            tileId={tileId}
            label={label}
            canEdit={canEdit}
            isRemoving={isRemoving}
            onEdit={onEdit}
            onRemove={onRemove}
          />
        </div>
      ) : null}
      <ByodKpiBox
        label={label}
        middleContent={middleContent}
        semantic={kpiSemantic}
        widgetIcon={widget?.widget_icon}
        kpiboxWrapperClassName={kpiCss.kpiTileKPIBox}
        isLoading={isLoading}
        bottomContent={trendContent}
      />
    </div>
  );
}

function TableTileView(props: DashboardTileProps) {
  const {
    tileId,
    widget,
    canEdit,
    tileRef,
    data,
    isError,
    isFetching,
    error,
    fetchStatus,
    onRetry,
    isRemoving,
    onEdit,
    onRemove,
    onColumnsChange,
    onEntityColumnsChange,
    onFetchData,
    onExport,
    onSchedule,
    search,
    onSearchChange,
    tableResetKey,
  } = props;
  const label = widget?.label ?? tileId;

  const isTableData = data?.widget_type === WIDGET_TYPE.TABLE;
  const tableColumns = isTableData ? data.columns : [];
  const tableRows = isTableData ? data.rows : [];
  const searchableColumns = isTableData
    ? data.table_config?.searchable_columns
    : undefined;
  const tableTotalCount = isTableData
    ? data.pagination?.total_count ?? data.rows.length
    : 0;

  const selectedColumns =
    (widget?.widget_config_json?.selected_columns as string[] | undefined) ??
    null;

  const selectedEntityColumns =
    (widget?.widget_config_json?.entities_columns as string[] | undefined) ??
    null;
  // Entity-attribute column options come from the response (hoisted by
  // flattenTableEntityAttributes at the normalize boundary). `undefined` means
  // the widget isn't configured for entity columns — the table hides the
  // "Entity Variable" picker rather than showing it disabled.
  const entityColumns = isTableData ? data.all_attributes_columns : undefined;

  const initialSort = React.useMemo(
    () => ({
      sortBy: (widget?.widget_config_json?.sort_by as string) ?? '',
      sortOrder:
        (widget?.widget_config_json?.sort_direction as
          | 'asc'
          | 'desc'
          | 'original') || 'original',
    }),
    [widget?.widget_config_json],
  );

  if (fetchStatus === 'idle' && !data && !isError) {
    return (
      <div
        ref={tileRef}
        className={classify(vizCss.tile, vizCss.tableTile)}
        data-tile-id={tileId}
        data-testid={`byod-table-tile-${tileId}-skeleton`}
      />
    );
  }

  if (isError) {
    return (
      <div
        ref={tileRef}
        className={classify(vizCss.tile, vizCss.tableTile)}
        data-tile-id={tileId}
        data-testid={`byod-table-tile-${tileId}`}
      >
        <div className={vizCss.tileHeader}>
          <div className={vizCss.tileMeta}>
            <BodyMediumBold>{label}</BodyMediumBold>
          </div>
          {canEdit && !isRemoving && (
            <VizTileActionsMenu
              tileId={tileId}
              label={label}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          )}
        </div>
        {isFetching ? (
          <div className={vizCss.tileLoading}>
            <CircularLoader size="small" />
          </div>
        ) : (
          <TileErrorRetry
            message={error?.message}
            onRetry={onRetry}
            testIdPrefix={`byod-table-tile-${tileId}`}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={tileRef}
      className={vizCss.tableTile}
      data-tile-id={tileId}
      data-testid={`byod-table-tile-${tileId}`}
    >
      <ByodTable
        label={label}
        columns={tableColumns}
        rows={tableRows}
        totalCount={tableTotalCount}
        selectedColumns={selectedColumns}
        onSelectedColumnsChange={onColumnsChange}
        entityColumns={entityColumns}
        selectedEntityColumns={selectedEntityColumns}
        onSelectedEntityColumnsChange={onEntityColumnsChange}
        initialSort={initialSort}
        onFetchData={onFetchData}
        isLoading={!data || isFetching}
        onEdit={canEdit && onEdit ? () => onEdit(tileId) : undefined}
        onRemove={canEdit && onRemove ? () => onRemove(tileId) : undefined}
        isRemoving={isRemoving}
        onExport={onExport}
        onSchedule={onSchedule}
        search={search}
        onSearchChange={onSearchChange}
        searchableColumns={searchableColumns}
        resetKey={tableResetKey}
      />
    </div>
  );
}

function ChartTileView(props: DashboardTileProps) {
  const {
    tileId,
    widget,
    canEdit,
    tileRef,
    data,
    isError,
    isFetching,
    error,
    fetchStatus,
    onRetry,
    isRemoving,
    onEdit,
    onRemove,
    onChartStyleChange,
  } = props;
  const {t} = useLabelTranslation();
  const label = widget?.label ?? tileId;
  const chartStyle =
    (widget?.widget_config_json?.chart_style as ChartStyle | undefined) ??
    undefined;

  const menuActions = React.useMemo<React.ReactNode>(() => {
    if (!canEdit) {
      return undefined;
    }
    return isRemoving ? (
      <CircularLoader size="small" />
    ) : (
      <VizTileActionsMenu
        tileId={tileId}
        label={label}
        onEdit={onEdit}
        onRemove={onRemove}
      />
    );
  }, [canEdit, isRemoving, tileId, label, onEdit, onRemove]);

  const isChartData = data?.widget_type === WIDGET_TYPE.CHART;
  const rawChartConfig = isChartData ? data.chart_config : null;
  const chartData = isChartData ? data.data : [];

  const savedXKeys = widget?.widget_config_json?.x_axis_keys;
  const savedYKeys = widget?.widget_config_json?.y_axis_keys;
  const chartConfig = React.useMemo(
    () =>
      resolveChartConfig(
        rawChartConfig,
        Array.isArray(savedXKeys) ? (savedXKeys as string[]) : [],
        Array.isArray(savedYKeys) ? (savedYKeys as string[]) : [],
      ),
    [rawChartConfig, savedXKeys, savedYKeys],
  );

  if (fetchStatus === 'idle' && !data && !isError) {
    return (
      <div
        ref={tileRef}
        className={classify(vizCss.tile, vizCss.tileChartWrapperDiv)}
        data-tile-id={tileId}
        data-testid={`byod-chart-tile-${tileId}-skeleton`}
      />
    );
  }

  if (isError) {
    // Render the shared danger error/retry (as KPI + table do) rather than
    // ChartWrapper's empty-data slot — a load failure must read as an error, not
    // as a legitimately-empty result, so the user knows to Retry.
    return (
      <div
        ref={tileRef}
        className={classify(vizCss.tile, vizCss.tileChartWrapperDiv)}
        data-tile-id={tileId}
        data-testid={`byod-chart-tile-${tileId}`}
      >
        <div className={vizCss.tileHeader}>
          <div className={vizCss.tileMeta}>
            <BodyMediumBold>{label}</BodyMediumBold>
          </div>
          {menuActions}
        </div>
        {isFetching ? (
          <div className={vizCss.tileLoading}>
            <CircularLoader size="small" />
          </div>
        ) : (
          <TileErrorRetry
            message={error?.message}
            onRetry={onRetry}
            testIdPrefix={`byod-chart-tile-${tileId}`}
          />
        )}
      </div>
    );
  }

  return (
    <div
      ref={tileRef}
      className={vizCss.tileChartWrapperDiv}
      data-tile-id={tileId}
    >
      <ByodChart
        label={label}
        chartConfig={chartConfig}
        data={chartData}
        chartStyle={chartStyle}
        onChartStyleChange={canEdit ? onChartStyleChange : undefined}
        headerActions={menuActions}
        chartHeight={CHART_TILE_HEIGHT}
        isLoading={!data || isFetching}
        classNames={{
          container: vizCss.tileChart,
          wrapper: vizCss.tileChartWrapper,
          content: vizCss.tileChartContent,
          highChart: vizCss.tileChartHighChart,
        }}
        testId={`byod-chart-tile-${tileId}`}
      />
    </div>
  );
}

/**
 * Tile shown when a placement's catalog definition was deleted (`is_orphaned`).
 * Read-only, never queries — a gentle "unavailable" note sized to the tile slot,
 * compact for KPI rows and an illustrated empty state for chart/table rows.
 * Part of the shared render path so the builder and the embed show it identically.
 */
export function UnavailableTile({
  tileId,
  label,
  tileKind,
}: {
  tileId: string;
  label: string;
  tileKind: 'kpi' | 'viz';
}) {
  const {t} = useLabelTranslation();
  const title = t('This_report_is_unavailable', 'This report is unavailable');

  if (tileKind === 'kpi') {
    return (
      <div
        className={kpiCss.kpiTile}
        data-tile-id={tileId}
        data-testid={`byod-unavailable-tile-${tileId}`}
      >
        <div className={kpiCss.kpiTileError}>
          <BodyMediumBold>{label}</BodyMediumBold>
          <BodySmall color="secondary" className={kpiCss.kpiTileErrorMessage}>
            {title}
          </BodySmall>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classify(vizCss.tile, vizCss.tableTile)}
      data-tile-id={tileId}
      data-testid={`byod-unavailable-tile-${tileId}`}
    >
      <div className={vizCss.tileHeader}>
        <div className={vizCss.tileMeta}>
          <BodyMediumBold>{label}</BodyMediumBold>
        </div>
      </div>
      <EmptyState
        title={title}
        description={t(
          'This_reports_definition_was_removed_from_the_catalog',
          'This report’s definition was removed from the catalog.',
        )}
        imageVariant="chart"
      />
    </div>
  );
}

export function DashboardTile(props: DashboardTileProps) {
  switch (props.widget.widget_type) {
    case WIDGET_TYPE.KPI:
      return <KpiTileView {...props} />;
    case WIDGET_TYPE.TABLE:
      return <TableTileView {...props} />;
    default:
      return <ChartTileView {...props} />;
  }
}
